import React, { PropsWithChildren, createContext, useContext, useState } from 'react'

export enum MaintenanceType {
  MessageWithStartDate = 'maintenance.MessageWithStartDate',
  MessageWithStartDateTime = 'maintenance.MessageWithStartDateTime',
  MessageWithEndDateTime = 'maintenance.MessageWithEndDateTime',
  MessageWithEndDate = 'maintenance.MessageWithEndDate',
}

export interface MaintenanceData {
  Message: string
  StartDateTime?: string
  EndDateTime?: string
}

export const formatMaintenanceDate = (dateString: string): string => {
  if (!dateString) {
    return ''
  }
  try {
    const date = new Date(dateString)
    const day = date.getUTCDate().toString().padStart(2, '0')
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
    const year = date.getUTCFullYear()
    return `${day}.${month}.${year}`
  } catch {
    return ''
  }
}

export const formatMaintenanceDateTime = (dateString: string): string => {
  if (!dateString) {
    return ''
  }
  try {
    const date = new Date(dateString)
    const day = date.getUTCDate().toString().padStart(2, '0')
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
    const year = date.getUTCFullYear()
    const hours = date.getUTCHours().toString().padStart(2, '0')
    const minutes = date.getUTCMinutes().toString().padStart(2, '0')
    return `${day}.${month}.${year} ${hours}:${minutes} UTC`
  } catch {
    return ''
  }
}

export const processMaintenanceMessage = (maintenance: MaintenanceData): MaintenanceProps => {
  const { Message, StartDateTime, EndDateTime } = maintenance

  // return processed message with date substitutions
  const processedMessage = Message

  if (Message === MaintenanceType.MessageWithStartDate && StartDateTime && EndDateTime) {
    const startDate = formatMaintenanceDate(StartDateTime)
    const endDate = formatMaintenanceDate(EndDateTime)
    return {
      Message: 'maintenance.MessageWithStartDate',
      StartDateTime: StartDateTime || '',
      EndDateTime: EndDateTime || '',
      processedMessage,
      startDate,
      endDate,
    }
  }

  if (Message === MaintenanceType.MessageWithEndDate && EndDateTime) {
    const endDate = formatMaintenanceDate(EndDateTime)
    return {
      Message: 'maintenance.MessageWithEndDate',
      StartDateTime: StartDateTime || '',
      EndDateTime: EndDateTime || '',
      processedMessage,
      endDate,
    }
  }

  if (Message === MaintenanceType.MessageWithEndDateTime && EndDateTime) {
    const endDateTime = formatMaintenanceDateTime(EndDateTime)
    return {
      Message: 'maintenance.MessageWithEndDateTime',
      StartDateTime: StartDateTime || '',
      EndDateTime: EndDateTime || '',
      processedMessage,
      endDateTime,
    }
  }

  if (Message === MaintenanceType.MessageWithStartDateTime && StartDateTime && EndDateTime) {
    const startDateTime = formatMaintenanceDateTime(StartDateTime)
    const endDateTime = formatMaintenanceDateTime(EndDateTime)
    return {
      Message: 'maintenance.MessageWithStartDateTime',
      StartDateTime: StartDateTime || '',
      EndDateTime: EndDateTime || '',
      processedMessage,
      startDateTime,
      endDateTime,
    }
  }

  return {
    Message: processedMessage,
    StartDateTime: StartDateTime || '',
    EndDateTime: EndDateTime || '',
    processedMessage,
  }
}

export interface MaintenanceProps {
  Message: string
  StartDateTime: string
  EndDateTime: string
  processedMessage?: string
  startDate?: string
  endDate?: string
  endDateTime?: string
  startDateTime?: string
}

interface MaintenanceModeContextProps {
  maintenance: MaintenanceProps
  isMaintenanceMode: boolean
  setMaintenance(value?: MaintenanceData): void
}

export const initialMaintenanceState: MaintenanceProps = {
  Message: '',
  StartDateTime: '',
  EndDateTime: '',
}

export const MaintenanceModeContext = createContext<MaintenanceModeContextProps>({
  maintenance: initialMaintenanceState,
  isMaintenanceMode: false,
  setMaintenance: () => initialMaintenanceState,
})
MaintenanceModeContext.displayName = 'MaintenanceModeContext'

export const useMaintenance = (): MaintenanceModeContextProps => useContext(MaintenanceModeContext)

export const MaintenanceModeProvider: React.FC<PropsWithChildren> = (props) => {
  const { children } = props

  const [maintenance, setMaintenance] = useState<MaintenanceProps>(initialMaintenanceState)

  const handleMaintenance = (value?: MaintenanceData) => {
    if (value) {
      const processedMaintenance = processMaintenanceMessage(value)
      setMaintenance(processedMaintenance)
    } else {
      setMaintenance(initialMaintenanceState)
    }
  }

  return (
    <MaintenanceModeContext.Provider
      value={{
        maintenance,
        isMaintenanceMode: !!maintenance.Message,
        setMaintenance: handleMaintenance,
      }}
    >
      {children}
    </MaintenanceModeContext.Provider>
  )
}
